import { useState } from 'react';

import { PublicSuggestedConnection } from '@shared/types/userConnection';

import { useUserConnectionsQuery } from '@queries/UserConnectionsQueries';

import { useSendConnectionRequest } from '@mutations/UserConnectionMutations';

import { useAnalytics } from '@hooks/useAnalytics';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { findFirstAlphaCharacter } from '@utilities/findFirstAlphaCharacter';

import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { LetterAvatar } from '@components/layout/LetterAvatar/LetterAvatar';
import { Screen } from '@components/layout/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { UserAvatar } from '@components/layout/UserAvatar/UserAvatar';

const HIDDEN_STORAGE_KEY = 'hidden_suggested_connections';

const getHiddenIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem(HIDDEN_STORAGE_KEY);
    return new Set(stored ? JSON.parse(stored) : []);
  } catch {
    return new Set();
  }
};

const saveHiddenIds = (ids: Set<string>) => {
  localStorage.setItem(HIDDEN_STORAGE_KEY, JSON.stringify([...Array.from(ids)]));
};

export const SuggestedConnectionCard = ({
  connection,
  onDismiss,
}: {
  connection: PublicSuggestedConnection;
  onDismiss?: (userId: string) => void;
}) => {
  const { track } = useAnalytics();
  const { isSendingConnectionRequest, sendConnectionRequestAsync } = useSendConnectionRequest();

  const handleConnect = async () => {
    track('Send connection request', {
      toUserId: connection.connectionUserId,
    });

    await sendConnectionRequestAsync({ toUserId: connection.connectionUserId });
    onDismiss(connection.connectionUserId);
  };

  const handleHide = () => {
    track('Hide suggested connection', {
      toUserId: connection.connectionUserId,
    });

    const hidden = getHiddenIds();
    hidden.add(connection.connectionUserId);
    saveHiddenIds(hidden);
    onDismiss(connection.connectionUserId);
  };

  const mutualLabel =
    connection.mutualConnectionCount === 1
      ? '1 mutual connection'
      : `${connection.mutualConnectionCount} mutual connections`;

  return (
    <Card>
      <Card.Header
        avatar={
          <UserAvatar
            displayName={connection.connectionName}
            avatarUrl={connection.connectionAvatar}
          />
        }
        title={connection.connectionName}
        subtitle={mutualLabel}
      />
      <div className="d-flex flex-column gap-2 px-3 pb-3">
        <Button size="sm" disabled={isSendingConnectionRequest} onClick={handleConnect}>
          {isSendingConnectionRequest ? (
            <FontAwesomeIcon name="spinner" className="fa-spin" />
          ) : (
            'Connect'
          )}
        </Button>
        {onDismiss && (
          <Button size="sm" variant="outlined" color="neutral" onClick={handleHide}>
            Hide
          </Button>
        )}
      </div>
    </Card>
  );
};

export const SuggestedConnectionsScreen = createScreenComponent(() => {
  const { suggestedConnections } = useUserConnectionsQuery();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(getHiddenIds);

  const handleDismiss = (userId: string) => {
    setDismissedIds((prev) => new Set([...Array.from(prev), userId]));
  };

  const visible = suggestedConnections.filter((c) => !dismissedIds.has(c.connectionUserId));

  return (
    <Screen
      name="Suggested Connections Screen"
      headerProps={{
        back: { to: '/manage/connections' },
        title: '',
      }}
    >
      <Screen.Content>
        <TitleToolbar text="Suggested Connections" size="md" />
        {visible.length === 0 ? (
          <EmptyState
            title="No suggested connections right now."
            text="We'll show you more as you connect with more people."
          />
        ) : (
          <div className="d-grid gap-2">
            {visible.map((connection) => (
              <div key={connection.connectionUserId}>
                <SuggestedConnectionCard connection={connection} onDismiss={handleDismiss} />
              </div>
            ))}
          </div>
        )}
      </Screen.Content>
    </Screen>
  );
});
